"use client";

import { getTranslatedProductCategoryName } from "@/app/directories/shop/helper";
import {
  getCategories,
  getProductsByCategoryId,
} from "@frontend/lib/actions/actions";
import { getOrganizationAssetsForOrg } from "@frontend/lib/actions/config-actions";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useLocale } from "next-intl";
import { ProductCardCarousel } from "./ProductCardCarousel";

interface ProductCardCarouselWrapperClientProps {
  params: { clientSlug: string; orgId: number; kioskMode?: boolean };
  props: {
    categoryName: string;
    isMinimal?: boolean;
    showIcon?: boolean;
    iconURL?: string;
  };
  style?: string;
}

export function ProductCardCarouselWrapperClient({
  params,
  props,
  style,
}: ProductCardCarouselWrapperClientProps) {
  const locale = useLocale();
  const languageCode = locale.replace("-", "_");

  // Fetch organization assets
  const { data: organizationAssets } = useQuery({
    queryKey: ["organizationAssets", params.orgId],
    queryFn: () => getOrganizationAssetsForOrg(params.orgId),
    staleTime: 5 * 60 * 1000,
  });

  // Fetch categories
  const { data: categories, isLoading: categoriesLoading } = useQuery({
    queryKey: ["categories", params.orgId],
    queryFn: () => getCategories(params.orgId),
    staleTime: 5 * 60 * 1000,
    select: (data) =>
      data
        .sort((a, b) => b.popularity - a.popularity)
        .filter((category) => category.name === props.categoryName),
  });

  // Fetch products based on categories
  const { data: products, isLoading: productsLoading } = useQuery({
    queryKey: ["products", categories?.map((c) => c.id)],
    queryFn: () =>
      getProductsByCategoryId(categories?.map((category) => category.id) || []),
    enabled: !!categories?.length,
    staleTime: 5 * 60 * 1000,
    select: (data) => data.sort((a, b) => b.popularity - a.popularity),
  });

  const translatedCategoryName = categories?.[0]
    ? getTranslatedProductCategoryName(categories[0], languageCode)
    : "";

  // Loading state
  if (categoriesLoading || productsLoading) {
    return (
      <div className="w-full max-w-[1200px] mx-auto mt-0 py-12 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  // No categories or products
  if (!categories?.length || !products?.length) {
    return null;
  }

  return (
    <ProductCardCarousel
      products={products}
      clientSlug={params.clientSlug}
      orgId={params.orgId}
      categoryName={translatedCategoryName}
      isMinimal={props.isMinimal}
      showIcon={props.showIcon}
      iconURL={props.iconURL}
      primaryColor={organizationAssets?.primaryColor}
      primaryBackgroundColor={organizationAssets?.primaryBackgroundColor}
      secondaryBackgroundColor={organizationAssets?.secondaryBackgroundColor}
      kioskMode={params.kioskMode}
    />
  );
}
